import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, FormControl,
  InputLabel, Select, MenuItem, IconButton, Box, Typography
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import supabase from '../../../../utils/supabase';
import ScientificNameSelector from './parts/ScientificNameSelector';
import PublicationSelector from './parts/PublicationSelector';

export default function DialogTaxonomicActEditor({
  open,
  onClose,
  onSave,
  act, // if editing
  currentNameId, // FK (The ID of the name currently being edited in the parent dialog)
  currentNameLabel, // Display name for the current name
  publications = [], // passed from parent
}) {
  const [form, setForm] = useState({
    act_type_id: '',
    other_name_id: null, // This will be either scientific_name_id or related_name_id depending on direction
    publication_id: null,
    page: '',
    remarks: '',
  });
  const [actTypes, setActTypes] = useState([]);
  // false: Current Name -> Act -> Other Name (Current is Subject)
  // true: Other Name -> Act -> Current Name (Current is Object)
  const [isReverse, setIsReverse] = useState(false);

  const selectedActType = actTypes.find(t => t.id === form.act_type_id);
  const isNewDescription = selectedActType?.name === 'New Taxon Description';

  useEffect(() => {
    if (isNewDescription) setIsReverse(false);
  }, [isNewDescription]);

  useEffect(() => {
    if (open) {
      // Load act types
      supabase.from('taxonomic_act_types').select('*').order('name').then(({ data, error }) => {
        if (error) {
          console.error("Failed to load act_types:", error);
        }
        if (data) setActTypes(data);
      });

      if (act) {
        // Determine direction based on whether the current name is the subject or object
        const reverse = act.scientific_name_id !== currentNameId;
        setIsReverse(reverse);
        setForm({
          act_type_id: act.act_type_id || '',
          other_name_id: reverse ? act.scientific_name_id : act.related_name_id,
          publication_id: act.publication_id || null,
          page: act.page || '',
          remarks: act.remarks || '',
        });
      } else {
        setIsReverse(false);
        setForm({
          act_type_id: '',
          other_name_id: null,
          publication_id: null,
          page: '',
          remarks: '',
        });
      }
    }
  }, [open, act]);

  const handleSave = async () => {
    if (!form.act_type_id) {
      alert("Please select an Act Type.");
      return;
    }
    if (isReverse && !isNewDescription && !form.other_name_id) {
      alert("Please select the Subject name.");
      return;
    }
    if (!form.publication_id) {
      alert("Please select a Publication.");
      return;
    }

    let pubUuid = publications.find(p => p.id === form.publication_id)?.id_uuid;
    if (!pubUuid && form.publication_id) {
      const { data } = await supabase.from('publications').select('id_uuid').eq('id', form.publication_id).maybeSingle();
      pubUuid = data?.id_uuid;
    }
    if (!pubUuid) {
      alert("Selected publication is invalid or missing UUID.");
      return;
    }

    const payload = {
      scientific_name_id: isReverse ? form.other_name_id : currentNameId,
      act_type_id: form.act_type_id,
      related_name_id: isReverse ? currentNameId : form.other_name_id,
      publication_id: form.publication_id,
      publication_id_uuid: pubUuid,
      page: form.page,
      remarks: form.remarks,
    };

    let error;
    if (act?.id) {
      const { error: e } = await supabase.from('taxonomic_acts').update(payload).eq('id', act.id);
      error = e;
    } else {
      const { error: e } = await supabase.from('taxonomic_acts').insert([payload]);
      error = e;
    }

    if (error) {
      alert('Error saving act: ' + error.message);
    } else {
      onSave && onSave();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{act ? 'Edit Taxonomic Act' : 'Add Taxonomic Act'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          
          {/* Direction / Relationship Builder */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" display="block" color="text.secondary">Subject (Subject of the Act)</Typography>
              {isReverse ? (
                <ScientificNameSelector
                  valueId={form.other_name_id}
                  onChange={(id) => setForm({ ...form, other_name_id: id })}
                  showInputLabel={false}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  value={currentNameLabel || "(This Name)"}
                  disabled
                  variant="filled"
                />
              )}
            </Box>

            <Stack alignItems="center">
              <Typography variant="caption" sx={{ mb: 0.5 }}>Direction</Typography>
              <IconButton 
                onClick={() => setIsReverse(!isReverse)} 
                color="primary" 
                disabled={isNewDescription}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <SwapHorizIcon />
              </IconButton>
            </Stack>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" display="block" color="text.secondary">Object (Target/Related Name)</Typography>
              {isReverse ? (
                <TextField
                  fullWidth
                  size="small"
                  value={currentNameLabel || "(This Name)"}
                  disabled
                  variant="filled"
                />
              ) : (
                <ScientificNameSelector
                  valueId={form.other_name_id}
                  onChange={(id) => setForm({ ...form, other_name_id: id })}
                  showInputLabel={false}
                />
              )}
            </Box>
          </Stack>

          <FormControl fullWidth size="small">
            <InputLabel id="act-type-label">Act Type</InputLabel>
            <Select
              labelId="act-type-label"
              value={form.act_type_id}
              label="Act Type"
              onChange={(e) => setForm({ ...form, act_type_id: e.target.value })}
            >
              {actTypes.length === 0 && <MenuItem disabled>Loading...</MenuItem>}
              {actTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <PublicationSelector
            label="Publication"
            value={form.publication_id}
            onChange={(id) => setForm({ ...form, publication_id: id })}
            publicationsData={publications}
            required
          />

          <TextField
            label="Page"
            value={form.page}
            onChange={(e) => setForm({ ...form, page: e.target.value })}
            size="small"
          />
           <TextField
            label="Note"
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            multiline
            rows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}